
import React, { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { RotateCcw, AlertTriangle, CheckCircle, Trash2, Clock } from "lucide-react";
import { format } from "date-fns";

export default function RestoreData() {
  const [message, setMessage] = useState(null);
  const [selectedCloseout, setSelectedCloseout] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me(),
  });

  const isMainAdmin = user?.role === 'admin';

  const { data: closeouts = [] } = useQuery({
    queryKey: ['closeouts'],
    queryFn: () => base44.entities.Closeout.list('-created_date'),
  });

  const { data: rosterEntries = [] } = useQuery({
    queryKey: ['rosterEntries'],
    queryFn: () => base44.entities.RosterEntry.list(),
  });

  const restoreFromSnapshotMutation = useMutation({
    mutationFn: async (closeoutId) => {
      const closeout = closeouts.find(c => c.id === closeoutId);
      if (!closeout || !closeout.roster_snapshot) {
        throw new Error('No snapshot found for this closeout');
      }

      console.log('🔄 Starting restore from snapshot...');
      
      const snapshot = JSON.parse(closeout.roster_snapshot);
      const eaRoster = snapshot.ea_roster || [];
      const asdRoster = snapshot.asd_roster || [];

      console.log(`📊 Snapshot contains ${eaRoster.length} EA entries and ${asdRoster.length} ASD entries`);

      // Delete all current roster entries in batches
      const allEntries = await base44.entities.RosterEntry.list();
      console.log(`🗑️ Deleting ${allEntries.length} current entries...`);
      
      const deleteBatchSize = 100;
      for (let i = 0; i < allEntries.length; i += deleteBatchSize) {
        const batch = allEntries.slice(i, i + deleteBatchSize);
        await Promise.all(batch.map(entry => base44.entities.RosterEntry.delete(entry.id)));
        console.log(`🗑️ Deleted ${Math.min(i + deleteBatchSize, allEntries.length)}/${allEntries.length} entries`);
      }

      // Restore EA roster entries in batches - ENSURE STATUS IS ACTIVE OR EXCUSED
      console.log(`📥 Restoring ${eaRoster.length} EA entries...`);
      const createBatchSize = 100;
      
      for (let i = 0; i < eaRoster.length; i += createBatchSize) {
        const batch = eaRoster.slice(i, i + createBatchSize);
        const entriesToCreate = batch.map(entry => {
          const newEntry = { ...entry };
          delete newEntry.id;
          delete newEntry.created_date;
          delete newEntry.updated_date;
          delete newEntry.created_by;
          
          // CRITICAL FIX: Ensure EA roster entries have correct status
          // Only allow 'active' or 'excused' status for EA roster
          if (newEntry.status !== 'active' && newEntry.status !== 'excused') {
            console.warn(`⚠️ EA entry had invalid status '${newEntry.status}', changing to 'active'`);
            newEntry.status = 'active';
          }
          
          return newEntry;
        });
        
        await base44.entities.RosterEntry.bulkCreate(entriesToCreate);
        console.log(`📥 Restored ${Math.min(i + createBatchSize, eaRoster.length)}/${eaRoster.length} EA entries`);
      }

      // Restore ASD roster entries in batches - ENSURE STATUS IS ASD
      console.log(`📥 Restoring ${asdRoster.length} ASD entries...`);
      
      for (let i = 0; i < asdRoster.length; i += createBatchSize) {
        const batch = asdRoster.slice(i, i + createBatchSize);
        const entriesToCreate = batch.map(entry => {
          const newEntry = { ...entry };
          delete newEntry.id;
          delete newEntry.created_date;
          delete newEntry.updated_date;
          delete newEntry.created_by;
          
          // CRITICAL FIX: Ensure ASD roster entries have status 'asd'
          if (newEntry.status !== 'asd') {
            console.warn(`⚠️ ASD entry had invalid status '${newEntry.status}', changing to 'asd'`);
          }
          newEntry.status = 'asd';
          
          return newEntry;
        });
        
        await base44.entities.RosterEntry.bulkCreate(entriesToCreate);
        console.log(`📥 Restored ${Math.min(i + createBatchSize, asdRoster.length)}/${asdRoster.length} ASD entries`);
      }

      console.log('✅ Restore complete!');

      return {
        eaRestored: eaRoster.length,
        asdRestored: asdRoster.length,
        date: closeout.closeout_date
      };
    },
    onSuccess: ({ eaRestored, asdRestored, date }) => {
      setMessage({
        type: 'success',
        text: `✅ Successfully restored from ${date} snapshot! Restored ${eaRestored} EA entries and ${asdRestored} ASD entries.`
      });
      queryClient.invalidateQueries(['rosterEntries']);
      setShowConfirm(false);
      setSelectedCloseout(null);
      setTimeout(() => setMessage(null), 8000);
    },
    onError: (err) => {
      console.error('Restore error:', err);
      setMessage({
        type: 'error',
        text: `❌ Restore failed: ${err.message}`
      });
      setShowConfirm(false);
      setTimeout(() => setMessage(null), 5000);
    }
  });

  const clearAllEntriesMutation = useMutation({
    mutationFn: async () => {
      const allEntries = await base44.entities.RosterEntry.list();
      console.log(`🗑️ Deleting ${allEntries.length} entries...`);
      
      const batchSize = 100;
      for (let i = 0; i < allEntries.length; i += batchSize) {
        const batch = allEntries.slice(i, i + batchSize);
        await Promise.all(batch.map(entry => base44.entities.RosterEntry.delete(entry.id)));
        console.log(`🗑️ Deleted ${Math.min(i + batchSize, allEntries.length)}/${allEntries.length} entries`);
      }
      
      return allEntries.length;
    },
    onSuccess: (count) => {
      setMessage({
        type: 'success',
        text: `✅ Successfully deleted all ${count} roster entries. You can now start fresh.`
      });
      queryClient.invalidateQueries(['rosterEntries']);
      setTimeout(() => setMessage(null), 5000);
    },
    onError: (err) => {
      setMessage({
        type: 'error',
        text: `❌ Failed to clear entries: ${err.message}`
      });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  const clearASDRosterMutation = useMutation({
    mutationFn: async () => {
      const asdEntries = await base44.entities.RosterEntry.filter({ status: 'asd' });
      console.log(`🗑️ Deleting ${asdEntries.length} ASD entries...`);
      
      const batchSize = 100;
      for (let i = 0; i < asdEntries.length; i += batchSize) {
        const batch = asdEntries.slice(i, i + batchSize);
        await Promise.all(batch.map(entry => base44.entities.RosterEntry.delete(entry.id)));
        console.log(`🗑️ Deleted ${Math.min(i + batchSize, asdEntries.length)}/${asdEntries.length} ASD entries`);
      }
      
      return asdEntries.length;
    },
    onSuccess: (count) => {
      setMessage({
        type: 'success',
        text: `✅ Successfully deleted all ${count} ASD roster entries. EA roster is untouched.`
      });
      queryClient.invalidateQueries(['rosterEntries']);
      setTimeout(() => setMessage(null), 5000);
    },
    onError: (err) => {
      setMessage({
        type: 'error',
        text: `❌ Failed to clear ASD roster: ${err.message}`
      });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  const resetStatusToActiveMutation = useMutation({
    mutationFn: async () => {
      const allEntries = await base44.entities.RosterEntry.list();
      const completedEntries = allEntries.filter(e => e.status === 'completed');
      
      console.log(`🔄 Resetting ${completedEntries.length} completed entries...`);
      
      const batchSize = 100;
      for (let i = 0; i < completedEntries.length; i += batchSize) {
        const batch = completedEntries.slice(i, i + batchSize);
        await Promise.all(batch.map(entry => 
          base44.entities.RosterEntry.update(entry.id, {
            status: 'active',
            attendance_status: 'pending'
          })
        ));
        console.log(`🔄 Reset ${Math.min(i + batchSize, completedEntries.length)}/${completedEntries.length} entries`);
      }
      
      return completedEntries.length;
    },
    onSuccess: (count) => {
      setMessage({
        type: 'success',
        text: `✅ Successfully reset ${count} completed entries back to active/pending status.`
      });
      queryClient.invalidateQueries(['rosterEntries']);
      setTimeout(() => setMessage(null), 5000);
    },
    onError: (err) => {
      setMessage({
        type: 'error',
        text: `❌ Failed to reset status: ${err.message}`
      });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  const removeASDDuplicatesMutation = useMutation({
    mutationFn: async () => {
      console.log('🔍 Starting ASD duplicate removal...');
      
      const asdEntries = await base44.entities.RosterEntry.filter({ status: 'asd' });
      console.log(`📊 Found ${asdEntries.length} ASD entries to check`);
      
      const seen = new Map();
      const duplicateIds = [];

      for (const entry of asdEntries) {
        // Create unique key based on student + teacher + course
        const key = `${entry.other_id}|${entry.teacher}|${entry.course_short_description}`;
        
        if (seen.has(key)) {
          duplicateIds.push(entry.id);
          console.log(`🔄 Duplicate found: ${entry.student_first_name} ${entry.student_last_name} - ${entry.teacher} - ${entry.course_short_description}`);
        } else {
          seen.set(key, entry.id);
        }
      }

      console.log(`🗑️ Removing ${duplicateIds.length} duplicates in batches...`);
      
      if (duplicateIds.length === 0) {
        console.log('✅ No duplicates found!');
        return 0;
      }
      
      const batchSize = 100;
      for (let i = 0; i < duplicateIds.length; i += batchSize) {
        const batch = duplicateIds.slice(i, i + batchSize);
        await Promise.all(batch.map(id => base44.entities.RosterEntry.delete(id)));
        console.log(`🗑️ Deleted ${Math.min(i + batchSize, duplicateIds.length)}/${duplicateIds.length} duplicates`);
      }

      console.log('✅ Duplicate removal complete!');
      return duplicateIds.length;
    },
    onSuccess: (count) => {
      if (count === 0) {
        setMessage({
          type: 'success',
          text: `✅ No duplicate ASD entries found. Your ASD roster is clean!`
        });
      } else {
        setMessage({
          type: 'success',
          text: `✅ Successfully removed ${count} duplicate ASD entries.`
        });
      }
      queryClient.invalidateQueries(['rosterEntries']);
      setTimeout(() => setMessage(null), 5000);
    },
    onError: (err) => {
      console.error('❌ Duplicate removal error:', err);
      setMessage({
        type: 'error',
        text: `❌ Failed to remove duplicates: ${err.message}`
      });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  if (!isMainAdmin) {
    return (
      <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen flex items-center justify-center">
        <Alert variant="destructive" className="max-w-md">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>
            You do not have permission to access data restoration.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Current roster stats
  const activeEA = rosterEntries.filter(e => e.status === 'active' || e.status === 'excused');
  const completedEA = rosterEntries.filter(e => e.status === 'completed');
  const currentASD = rosterEntries.filter(e => e.status === 'asd');

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Data Restoration</h1>
          <p className="text-slate-600 mt-1">Recover from Failed Closeouts or Fix Data Issues</p>
        </div>

        {message && (
          <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
            {message.type === 'success' ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-red-600" />
            )}
            <AlertDescription className={message.type === 'success' ? 'text-green-900' : 'text-red-900'}>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        {completedEA.length > 0 && (
          <Alert className="bg-orange-50 border-orange-200">
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription className="text-orange-900">
              <strong>⚠️ Partial Closeout Detected!</strong>
              <div className="mt-2 space-y-1 text-sm">
                <p>Found {completedEA.length} entries marked as "completed" - this suggests a closeout started but didn't finish properly.</p>
                <p className="font-semibold mt-2">To recover:</p>
                <ol className="list-decimal list-inside ml-2 space-y-1">
                  <li>Click <strong>"Reset Completed → Active"</strong> below to restore the {completedEA.length} entries</li>
                  <li>Check the EA Roster to verify your data is back</li>
                  <li>If you see duplicate ASD entries, use <strong>"Remove ASD Duplicates"</strong></li>
                  <li>Then you can re-run the closeout properly</li>
                </ol>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Current State Overview */}
        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle>Current Roster State</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700 font-medium">Active EA</p>
                <p className="text-3xl font-bold text-blue-900">{activeEA.length}</p>
                <p className="text-xs text-blue-600 mt-1">Current EA roster entries</p>
              </div>
              <div className={`p-4 rounded-lg ${completedEA.length > 0 ? 'bg-orange-50' : 'bg-green-50'}`}>
                <p className={`text-sm font-medium ${completedEA.length > 0 ? 'text-orange-700' : 'text-green-700'}`}>
                  Completed EA
                </p>
                <p className={`text-3xl font-bold ${completedEA.length > 0 ? 'text-orange-900' : 'text-green-900'}`}>
                  {completedEA.length}
                </p>
                <p className={`text-xs mt-1 ${completedEA.length > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                  {completedEA.length > 0 ? '⚠️ Needs attention' : 'Normal after closeout'}
                </p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-red-700 font-medium">ASD</p>
                <p className="text-3xl font-bold text-red-900">{currentASD.length}</p>
                <p className="text-xs text-red-600 mt-1">Refusal roster entries</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Fix Actions */}
        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle>Quick Fix Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Button
                onClick={() => {
                  if (confirm(`Reset ${completedEA.length} completed entries back to active/pending? This will undo the "completed" status from the failed closeout.`)) {
                    resetStatusToActiveMutation.mutate();
                  }
                }}
                className={`${completedEA.length > 0 ? 'bg-orange-600 hover:bg-orange-700 animate-pulse' : 'bg-orange-600 hover:bg-orange-700'} h-auto py-4 flex flex-col items-start gap-2`}
                disabled={resetStatusToActiveMutation.isPending || completedEA.length === 0}
              >
                <Clock className="w-5 h-5" />
                <div className="text-left">
                  <div className="font-semibold">Reset Completed → Active</div>
                  <div className="text-xs font-normal opacity-90">
                    {completedEA.length > 0 ? `⚠️ ${completedEA.length} entries need reset` : 'No entries to reset'}
                  </div>
                </div>
              </Button>

              <Button
                onClick={() => {
                  if (confirm(`Remove duplicate ASD entries (same student + teacher + course)?\n\nThis will scan all ${currentASD.length} ASD entries and remove duplicates.`)) {
                    removeASDDuplicatesMutation.mutate();
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700 h-auto py-4 flex flex-col items-start gap-2"
                disabled={removeASDDuplicatesMutation.isPending}
              >
                <Trash2 className="w-5 h-5" />
                <div className="text-left">
                  <div className="font-semibold">Remove ASD Duplicates</div>
                  <div className="text-xs font-normal opacity-90">
                    {removeASDDuplicatesMutation.isPending ? 'Scanning...' : `Check ${currentASD.length} entries`}
                  </div>
                </div>
              </Button>

              <Button
                onClick={() => {
                  if (confirm(`⚠️ DELETE ALL ${currentASD.length} ASD roster entries?\n\nThis will delete the entire ASD roster but leave EA roster untouched.\n\nYou can then reupload a fresh ASD report.`)) {
                    clearASDRosterMutation.mutate();
                  }
                }}
                className="bg-red-600 hover:bg-red-700 h-auto py-4 flex flex-col items-start gap-2"
                disabled={clearASDRosterMutation.isPending || currentASD.length === 0}
              >
                <Trash2 className="w-5 h-5" />
                <div className="text-left">
                  <div className="font-semibold">Clear ASD Roster</div>
                  <div className="text-xs font-normal opacity-90">
                    {currentASD.length > 0 ? `Delete ${currentASD.length} ASD entries` : 'No ASD entries'}
                  </div>
                </div>
              </Button>

              <Button
                onClick={() => {
                  if (confirm(`⚠️ DELETE ALL ${rosterEntries.length} roster entries? This cannot be undone unless you restore from a snapshot!`)) {
                    if (confirm('Are you ABSOLUTELY SURE? This will delete everything!')) {
                      clearAllEntriesMutation.mutate();
                    }
                  }
                }}
                variant="destructive"
                className="h-auto py-4 flex flex-col items-start gap-2"
                disabled={clearAllEntriesMutation.isPending}
              >
                <Trash2 className="w-5 h-5" />
                <div className="text-left">
                  <div className="font-semibold">Clear All Entries</div>
                  <div className="text-xs font-normal opacity-90">⚠️ Nuclear option</div>
                </div>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Restore from Snapshots */}
        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle>Restore from Closeout Snapshot</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert className="bg-blue-50 border-blue-200 mb-4">
              <AlertDescription className="text-blue-900">
                <strong>ℹ️ About Snapshots:</strong> Snapshots are only created when a closeout completes successfully. If your closeout failed partway through, it won't appear here. Use the "Quick Fix Actions" above instead.
              </AlertDescription>
            </Alert>

            <Alert className="bg-yellow-50 border-yellow-200 mb-4">
              <AlertTriangle className="w-4 h-4 text-yellow-600" />
              <AlertDescription className="text-yellow-900">
                <strong>Warning:</strong> Restoring from a snapshot will DELETE all current roster entries and replace them with the snapshot data. Make sure you select the correct snapshot!
              </AlertDescription>
            </Alert>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead>EA Count</TableHead>
                    <TableHead>ASD Count</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {closeouts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        No snapshots available
                      </TableCell>
                    </TableRow>
                  ) : (
                    closeouts.map((closeout) => (
                      <TableRow key={closeout.id} className="hover:bg-slate-50">
                        <TableCell className="font-medium">
                          {format(new Date(closeout.closeout_date), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell>{closeout.closeout_time}</TableCell>
                        <TableCell>
                          <Badge className="bg-blue-100 text-blue-800">
                            {closeout.ea_assigned_count || 0}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-red-100 text-red-800">
                            {closeout.asd_assigned_count || 0}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm max-w-xs truncate">
                          {closeout.notes || '-'}
                        </TableCell>
                        <TableCell>
                          <Button
                            onClick={() => {
                              setSelectedCloseout(closeout);
                              setShowConfirm(true);
                            }}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            disabled={restoreFromSnapshotMutation.isPending}
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Restore
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Confirmation Dialog */}
        {showConfirm && selectedCloseout && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-lg">
              <CardHeader className="border-b bg-yellow-50">
                <CardTitle className="flex items-center gap-2 text-yellow-900">
                  <AlertTriangle className="w-5 h-5" />
                  Confirm Restore
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <Alert className="bg-red-50 border-red-200">
                  <AlertDescription className="text-red-900">
                    <strong>⚠️ This will:</strong>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>DELETE all {rosterEntries.length} current roster entries</li>
                      <li>Restore {selectedCloseout.ea_assigned_count} EA entries from {format(new Date(selectedCloseout.closeout_date), 'MMM d, yyyy')}</li>
                      <li>Restore {selectedCloseout.asd_assigned_count} ASD entries from {format(new Date(selectedCloseout.closeout_date), 'MMM d, yyyy')}</li>
                      <li>This action CANNOT be undone (unless you restore from another snapshot)</li>
                    </ul>
                  </AlertDescription>
                </Alert>
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowConfirm(false);
                      setSelectedCloseout(null);
                    }}
                    className="flex-1"
                    disabled={restoreFromSnapshotMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => restoreFromSnapshotMutation.mutate(selectedCloseout.id)}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    disabled={restoreFromSnapshotMutation.isPending}
                  >
                    {restoreFromSnapshotMutation.isPending ? "Restoring..." : "Confirm Restore"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
